import { TFunction } from 'i18next';
import { BatchReturnStatusType, ReturnItem, ReturnOrderLine, ReturnSlip, ReturnStatusType } from 'returnCenter-api';
import { ExtendedReturnItem, LoadingCards } from 'store/reducers/shared/sliceTypes';

export const getStatusTypeString = (statusType?: number): keyof LoadingCards | undefined => {
    switch (statusType) {
        case ReturnStatusType.InProgress:
            return 'inprogress';
        case ReturnStatusType.OnHold:
            return 'onHold';
        case ReturnStatusType.Completed:
            return 'completed';
        case ReturnStatusType.Created:
            return 'created';
        case ReturnStatusType.ItemsSelected:
            return 'itemsSelected';
        case ReturnStatusType.IncomingPackage:
            return 'incomingPackage';
        case ReturnStatusType.Received:
            return 'received';
        case ReturnStatusType.CreditError:
            return 'creditError';
        case ReturnStatusType.Processing:
            return 'processing';
        case ReturnStatusType.CreditedBatchPending:
            return 'creditedBatchPending';
        default:
            return undefined;
    }
};

export const getStatsTranslations = (t: TFunction, type?: ReturnStatusType) => {
    switch (type) {
        case ReturnStatusType.Completed:
            return t('returnStatus.completedReturns');
        case ReturnStatusType.InProgress:
            return t('returnStatus.inProgress');
        case ReturnStatusType.OnHold:
            return t('returnStatus.onHold');
        case ReturnStatusType.Created:
            return t('returnStatus.createdReturns');
        case ReturnStatusType.ItemsSelected:
            return t('returnStatus.itemsSelected');
        case ReturnStatusType.IncomingPackage:
            return t('returnStatus.incomingPackage');
        case ReturnStatusType.Received:
            return t('returnStatus.receivedReturns');
        case ReturnStatusType.CreditError:
            return t('returnStatus.creditError');
        case ReturnStatusType.Processing:
            return t('returnStatus.processing', 'Processing');
        case ReturnStatusType.CreditedBatchPending:
            return t('returnStatus.creditedBatchPending');
        default:
            return t('returnStatus.unknown', 'Not registered');
    }
};

// ============ BATCH RETURN HELPERS ============

export const getBatchStatusTypeString = (statusType?: BatchReturnStatusType): string | undefined => {
    switch (statusType) {
        case BatchReturnStatusType.Open:
            return 'open';
        case BatchReturnStatusType.Closed:
            return 'closed';
        case BatchReturnStatusType.Sent:
            return 'sent';
        case BatchReturnStatusType.PartiallyReceived:
            return 'partiallyReceived';
        case BatchReturnStatusType.Received:
            return 'received';
        case BatchReturnStatusType.PartiallyProcessed:
            return 'partiallyProcessed';
        case BatchReturnStatusType.Completed:
            return 'completed';
        case BatchReturnStatusType.Incoming:
            return 'incoming';
        default:
            return undefined;
    }
};

export const parseBatchStatusFromUrl = (str?: string): BatchReturnStatusType | undefined => {
    switch (str) {
        case 'open':
            return BatchReturnStatusType.Open;
        case 'closed':
            return BatchReturnStatusType.Closed;
        case 'sent':
            return BatchReturnStatusType.Sent;
        case 'partiallyReceived':
            return BatchReturnStatusType.PartiallyReceived;
        case 'received':
            return BatchReturnStatusType.Received;
        case 'partiallyProcessed':
            return BatchReturnStatusType.PartiallyProcessed;
        case 'completed':
            return BatchReturnStatusType.Completed;
        case 'incoming':
            return BatchReturnStatusType.Incoming;
        default:
            return undefined;
    }
};

export const getBatchStatsTranslations = (t: TFunction, type?: BatchReturnStatusType) => {
    switch (type) {
        case BatchReturnStatusType.Open:
            return t('batchReturnStatus.open', 'Open');
        case BatchReturnStatusType.Closed:
            return t('batchReturnStatus.closed', 'Closed');
        case BatchReturnStatusType.Sent:
            return t('batchReturnStatus.sent', 'Sent');
        case BatchReturnStatusType.PartiallyReceived:
            return t('batchReturnStatus.partiallyReceived', 'Partially received');
        case BatchReturnStatusType.Received:
            return t('batchReturnStatus.received', 'Received');
        case BatchReturnStatusType.PartiallyProcessed:
            return t('batchReturnStatus.partiallyProcessed', 'Partially processed');
        case BatchReturnStatusType.Completed:
            return t('batchReturnStatus.completed', 'Completed');
        case BatchReturnStatusType.Incoming:
            return t('batchReturnStatus.incoming', 'Incoming');
        default:
            return t('batchReturnStatus.unknown', 'Unknown');
    }
};

export const getBatchStatusInfo = (
    id: number | string | undefined,
    t: TFunction,
): { type: BatchReturnStatusType | undefined; heading: string } => {
    switch (id) {
        case 'open':
        case BatchReturnStatusType.Open:
            return { type: BatchReturnStatusType.Open, heading: t('batchReturnStatus.open', 'Open') };
        case 'closed':
        case BatchReturnStatusType.Closed:
            return { type: BatchReturnStatusType.Closed, heading: t('batchReturnStatus.closed', 'Closed') };
        case 'sent':
        case BatchReturnStatusType.Sent:
            return { type: BatchReturnStatusType.Sent, heading: t('batchReturnStatus.sent', 'Sent') };
        case 'partiallyReceived':
        case BatchReturnStatusType.PartiallyReceived:
            return {
                type: BatchReturnStatusType.PartiallyReceived,
                heading: t('batchReturnStatus.partiallyReceived', 'Partially received'),
            };
        case 'received':
        case BatchReturnStatusType.Received:
            return { type: BatchReturnStatusType.Received, heading: t('batchReturnStatus.received', 'Received') };
        case 'partiallyProcessed':
        case BatchReturnStatusType.PartiallyProcessed:
            return {
                type: BatchReturnStatusType.PartiallyProcessed,
                heading: t('batchReturnStatus.partiallyProcessed', 'Partially processed'),
            };
        case 'completed':
        case BatchReturnStatusType.Completed:
            return { type: BatchReturnStatusType.Completed, heading: t('batchReturnStatus.completed', 'Completed') };
        case 'incoming':
        case BatchReturnStatusType.Incoming:
            return { type: BatchReturnStatusType.Incoming, heading: t('batchReturnStatus.incoming', 'Incoming') };
        default:
            return { type: undefined, heading: t('error_occurred') };
    }
};

export const hasSplits = (item: ReturnOrderLine) => {
    return item.returnItems && item.returnItems.length > 1;
};

export const alignFlexStart = (item: ReturnItem) => (hasSplits(item) ? ('start' as const) : undefined);

export function validateReturnSelection(
    isReturnItemSelected: Record<string, boolean>,
    allItemsHaveReasonCodes: boolean,
    t: TFunction,
    toast: typeof import('react-toastify').toast,
): boolean {
    // Find the first order with no selected item
    const missingOrder = Object.entries(isReturnItemSelected).find(([, selected]) => !selected);

    if (missingOrder) {
        const [orderNumber] = missingOrder;
        toast.error(t('selectAtLeastOneItem', { orderNumber }), {
            autoClose: 3500,
            closeOnClick: true,
        });
        return false;
    }

    if (!allItemsHaveReasonCodes) {
        toast.error(t('allItemsMustHaveReasonCodes'), {
            autoClose: 3500,
            closeOnClick: true,
        });
        return false;
    }

    return true;
}

export const getOrderNumbers = (orders: string[]): { key: string; value: string } => {
    if (orders.length === 0) {
        return { key: 'noOrdersAvailable', value: '' };
    }

    return orders.length > 1 ? { key: 'orders', value: orders.join(', ') } : { key: 'order', value: orders[0] };
};

export const canSplitItem = (item: ReturnOrderLine): boolean => {
    if (!item.quantityShipped || !item.quantityAvailableForReturn) return false;
    return item.quantityAvailableForReturn > 1;
};

export const getCellPadding = (idx: number): string | undefined => (idx === 0 ? '0' : undefined);

export const canIncrementByScan = (
    returnSlip: ReturnSlip | null | undefined,
    scanKey: string,
): { canIncrement: true } | { canIncrement: false; sku: string; max: number } => {
    if (!returnSlip?.returnItems) return { canIncrement: false, sku: '', max: 0 };

    const lowerKey = scanKey.toLowerCase();
    let lastMatchSku = '';
    let lastMatchMax = 0;

    for (const orderGroup of returnSlip.returnItems) {
        for (const orderLine of orderGroup.orderLines ?? []) {
            const ean = orderLine.ean?.trim().toLowerCase();
            const sku = orderLine.sku?.trim().toLowerCase();
            if (ean !== lowerKey && sku !== lowerKey) continue;

            lastMatchSku = orderLine.sku ?? '';
            lastMatchMax = orderLine.quantityAvailableForReturn ?? 0;

            const usedQty = (orderLine.returnItems ?? []).reduce((sum, ri) => sum + (ri.quantityInReturn ?? 0), 0);
            if (usedQty < lastMatchMax) {
                return { canIncrement: true };
            }
        }
    }

    return { canIncrement: false, sku: lastMatchSku, max: lastMatchMax };
};

export const getStatusInfo = (
    id: number | string | undefined,
    t: TFunction,
): { type: ReturnStatusType | undefined; heading: string } => {
    switch (id) {
        case 'created':
        case ReturnStatusType.Created:
            return { type: ReturnStatusType.Created, heading: t('createdReturns') };
        case 'itemsSelected':
        case ReturnStatusType.ItemsSelected:
            return { type: ReturnStatusType.ItemsSelected, heading: t('itemsSelectedReturns') };
        case 'incomingPackage':
        case ReturnStatusType.IncomingPackage:
            return { type: ReturnStatusType.IncomingPackage, heading: t('incomingPackageReturns') };
        case 'received':
        case ReturnStatusType.Received:
            return { type: ReturnStatusType.Received, heading: t('receivedReturns') };
        case 'inprogress':
        case ReturnStatusType.InProgress:
            return { type: ReturnStatusType.InProgress, heading: t('inProgress') };
        case 'onHold':
        case ReturnStatusType.OnHold:
            return { type: ReturnStatusType.OnHold, heading: t('onHold') };
        case 'completed':
        case ReturnStatusType.Completed:
            return { type: ReturnStatusType.Completed, heading: t('completedReturns') };
        case 'creditError':
        case ReturnStatusType.CreditError:
            return { type: ReturnStatusType.CreditError, heading: t('creditError') };
        case 'creditedBatchPending':
        case ReturnStatusType.CreditedBatchPending:
            return { type: ReturnStatusType.CreditedBatchPending, heading: t('creditedBatchPending') };
        default:
            return { type: undefined, heading: t('error_occurred') };
    }
};

export const getReturnItemId = (item: ExtendedReturnItem): number | string => item.id ?? item.splitId ?? '';
